import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import {
  Entity,
  RELATION_CHILD_OF,
  RELATION_OWNER_OF,
} from '@backstage/catalog-model';
import { EntitySwitch } from '@backstage/plugin-catalog';
import { useEntity } from '@backstage/plugin-catalog-react';
import { isOwnerOf } from '@backstage/plugin-catalog-react/alpha';
import {
  Direction,
  EntityCatalogGraphCard,
} from '@backstage/plugin-catalog-graph';
import { EntityGroupProfileCard } from '@backstage/plugin-org';

import { EntityTeamPullRequestsCard } from '@backstage-community/plugin-github-pull-requests-board';
import {
  EntityGrafanaDashboardsCard,
  isDashboardSelectorAvailable,
} from '@backstage-community/plugin-grafana';

import {
  ApisCard,
  EntityLayout,
  isOrganization,
  isSolutionLineGroup,
  MembersListCard,
  TeamLibrariesCard,
  TeamServicesCard,
  TeamSlackCard,
  useEntities,
} from '@internal/plugin-medidata-catalog-react';
import {
  MaturityBreakdownTable,
  MaturitySummaryInfoCard,
} from '@internal/plugin-maturity';

import { entityWarningContent, maturitySummaryContent } from './commonContent';
import { getTeamReusableDashboards } from '../utils';

const TeamGrafanaDashboardsCard = () => {
  const { entity } = useEntity();

  const components = useEntities(
    ['Component'],
    ['metadata', 'relations'],
  ).filter(component => isOwnerOf(entity, component));

  const dashboards = getTeamReusableDashboards(components);

  return components.length > 0 ? (
    <EntityGrafanaDashboardsCard
      title="Vision Dashboards"
      paged
      pageSize={5}
      additionalDashboards={(_: Entity) => dashboards}
    />
  ) : (
    <></>
  );
};

export const groupPage = (
  <EntityLayout parentEntityRelations={[RELATION_CHILD_OF]}>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={1}>
        <Grid item xs={8} md={4}>
          <Stack spacing={1}>
            {entityWarningContent}
            <EntitySwitch>
              <EntitySwitch.Case if={isOrganization}>
                <EntityGroupProfileCard />
              </EntitySwitch.Case>
              <EntitySwitch.Case>
                <MembersListCard />
              </EntitySwitch.Case>
            </EntitySwitch>
          </Stack>
        </Grid>
        <Grid item xs={10} md={5}>
          <EntitySwitch>
            <EntitySwitch.Case if={isOrganization}>
              <MaturityBreakdownTable />
            </EntitySwitch.Case>
            <EntitySwitch.Case>
              <Stack spacing={1}>
                <ApisCard />
                <MaturityBreakdownTable />
              </Stack>
            </EntitySwitch.Case>
          </EntitySwitch>
        </Grid>
        <Grid item md={3} xs={6}>
          <EntitySwitch>
            <EntitySwitch.Case if={isOrganization}>
              <MaturitySummaryInfoCard />
            </EntitySwitch.Case>
            <EntitySwitch.Case>
              <Stack spacing={1}>
                <MaturitySummaryInfoCard />
                <TeamSlackCard />
                <TeamGrafanaDashboardsCard />
              </Stack>
            </EntitySwitch.Case>
          </EntitySwitch>
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/maturity" title="Maturity">
      {maturitySummaryContent}
    </EntityLayout.Route>

    <EntityLayout.Route
      path="/observability"
      title="Observability"
      if={(entity: Entity) => !isOrganization(entity)}
    >
      <EntitySwitch>
        <EntitySwitch.Case
          if={(entity: Entity) => Boolean(isDashboardSelectorAvailable(entity))}
        >
          <EntityGrafanaDashboardsCard
            title="Vision Dashboards"
            paged
            pageSize={10}
            searchable
            sortable
          />
        </EntitySwitch.Case>
      </EntitySwitch>
    </EntityLayout.Route>

    <EntityLayout.Route
      path="/ownership_report"
      title="Team Ownership Report"
      if={isSolutionLineGroup}
    >
      <Grid container spacing={1}>
        <Grid item xs={12} md={6}>
          <TeamServicesCard />
        </Grid>
        <Grid item xs={12} md={6}>
          <TeamLibrariesCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/diagram" title="Diagram">
      <EntityCatalogGraphCard
        direction={Direction.TOP_BOTTOM}
        title="System Diagram"
        height={700}
        relations={[RELATION_OWNER_OF]}
        unidirectional={false}
      />
    </EntityLayout.Route>

    <EntityLayout.Route path="/pull-requests" title="Pull requests">
      <Grid container spacing={1}>
        <Grid item xs={12} md={12}>
          <Typography>
            This list shows all the PRs in the repositories that your team owns.
            These PRs could come from members of your team or other people.
          </Typography>
        </Grid>
        <Grid item xs={12} md={12}>
          <EntityTeamPullRequestsCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);
